import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { MoonLoader } from 'react-spinners';
import LabelGroup from 'components/Label-Group';
import SelectInput from 'components/Select-Input';
import { CheckIcon } from 'assets/icons';
import { Kanban } from 'libs/kanban';

const ModalDeletePhase = ({
  isOpen,
  toggle,
  phase,
  setColumns,
  columns,
  setStatus,
  kanbanName,
}) => {
  const { t: translate } = useTranslation();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const accountParam = searchParams.get('id');
  const kanban = new Kanban(kanbanName, accountParam);
  const [step, setStep] = useState('idle'); // idle | transfer | success
  const [loading, setLoading] = useState('idle'); // idle | deleting | moving
  const [targetColumn, setTargetColumn] = useState(null);
  const [errors, setErrors] = useState({});
  const [count, setCount] = useState(0);

  const filteredColumns = columns.filter((col) => col?.id !== phase?.id);

  const showHeader = step !== 'success' && loading !== 'moving';
  const showFooter = step === 'idle' || (step === 'transfer' && loading !== 'moving');
  const isInitialStep = step === 'idle';
  const isTransferStep = step === 'transfer' && loading === 'idle';
  const isMovingStep = loading === 'moving' && step === 'transfer';
  const isTransferSuccessStep = step === 'success' && loading !== 'moving';

  const deletePhase = async () => {
    setLoading('deleting');
    try {
      if (!phase.isNew) {
        await kanban.deleteColumn(phase.id);
      }
      setColumns((prev) => prev.filter((col) => col.id !== phase.id));
      setStatus('excluded');
      toggle();
    } catch (error) {
      console.error('Error deleting phase:', error);
      setStatus('error');
    } finally {
      setLoading('idle');
      setStep('idle');
    }
  };

  const checkIfTransfer = async () => {
    setLoading('deleting');
    try {
      const column = await kanban.getColumn(phase.id);
      if (column?.length) {
        setCount(column.length);
        setStep('transfer');
        setLoading('idle');
      } else {
        await deletePhase();
      }
    } catch (error) {
      console.error('Error checking transfer:', error);
      setStep('idle');
      setLoading('idle');
      setStatus('error');
    }
  };

  const moveCampaigns = async () => {
    if (!targetColumn) {
      setErrors((prev) => ({
        ...prev,
        targetColumnId: { message: 'Required Field!' },
      }));
      return;
    }
    setLoading('moving');
    try {
      await kanban.transferItems(phase.id, targetColumn.value);

      setStep('success');
    } catch (error) {
      console.error('Error moving campaigns:', error);
      setStatus('error');
    } finally {
      setLoading('idle');
      setErrors({});
    }
  };

  return (
    <ModalEditPhaseStyled
      isOpen={isOpen}
      toggle={() => {
        if (loading === 'idle') toggle();
      }}
      onClosed={() => {
        setStep('idle');
      }}
      centered
      style={{
        width: isMovingStep || isTransferSuccessStep ? '22rem' : 'auto',
        minWidth: isMovingStep || isTransferSuccessStep ? '22rem' : 'auto',
      }}
    >
      {showHeader && (
        <ModalHeader
          toggle={() => {
            if (loading === 'idle') toggle();
          }}
        >
          {translate('Delete phase')}
        </ModalHeader>
      )}
      <ModalBody>
        {isInitialStep && (
          <div className="d-flex flex-column gap-3">
            <p>
              {translate('Are you sure you want to delete the phase')}{' '}
              <span className="fw-bold">{phase?.name}?</span>
            </p>
          </div>
        )}
        {isTransferStep && (
          <div className="d-flex flex-column gap-3">
            <div>
              <p className="m-0">
                <Trans
                  i18nKey="The {{phase}} phase you want to delete has {{count}} campaigns."
                  values={{
                    phase: phase?.name,
                    count: count || 0,
                  }}
                  components={{ strong: <span className="fw-bold" /> }}
                />
              </p>
              <p className="m-0">{translate('Choose a phase to move campaigns to')}:</p>
            </div>
            <LabelGroup htmlFor="targetColumnId" label="Move to:" column required size={12}>
              <SelectInput
                controlName="targetColumnId"
                value={targetColumn}
                onChange={(column) => setTargetColumn(column ? column : null)}
                options={filteredColumns.map((col) => ({
                  value: col.id,
                  label: col.name,
                }))}
                error={errors}
                required
              />
              {errors.targetColumnId && (
                <span style={{ color: 'red' }}>{translate(errors.targetColumnId.message)}</span>
              )}
            </LabelGroup>
          </div>
        )}
        {isMovingStep && (
          <div className="d-flex flex-column justify-content-center align-items-center p-3 gap-3">
            <MoonLoader color="#556ee6" size={100} speedMultiplier={0.5} />
            <p
              className="m-0"
              style={{
                fontSize: '1rem',
              }}
            >
              {translate('Moving campaigns')}
            </p>
            <p
              className="m-0"
              style={{
                color: '#718096',
              }}
            >
              {translate('This may take a few minutes.')}
            </p>
          </div>
        )}
        {isTransferSuccessStep && (
          <div className="d-flex flex-column justify-content-center align-items-center p-3 gap-3">
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ scale: '0.5', height: '6.25rem' }}
            >
              <CheckIcon />
            </div>
            <p
              className="m-0"
              style={{
                fontSize: '0.9rem',
              }}
            >
              {translate('Campaigns transferred successfully')}
            </p>
            <p
              className="m-0"
              style={{
                color: '#718096',
              }}
            >
              <Trans
                i18nKey="Do you want to delete the {{phase}} phase?"
                values={{
                  phase: phase?.name,
                }}
                components={{ strong: <span className="fw-bold" style={{ color: '#343A40' }} /> }}
              />
            </p>
            <Button
              color="outline"
              onClick={deletePhase}
              disabled={loading === 'deleting'}
              className="w-auto btn-delete"
            >
              {loading === 'deleting' ? (
                <i className="bx bx-loader-alt bx-spin" />
              ) : (
                translate('Delete phase')
              )}
            </Button>
          </div>
        )}
      </ModalBody>
      {showFooter && (
        <ModalFooter>
          <Button
            color="outline"
            onClick={toggle}
            disabled={loading === 'moving'}
            className="w-auto"
          >
            {translate('Cancel')}
          </Button>
          {step === 'transfer' && (
            <Button
              color="primary"
              onClick={moveCampaigns}
              disabled={loading === 'moving'}
              style={{ width: '7.5rem' }}
            >
              {loading === 'moving' ? (
                <i className="bx bx-loader-alt bx-spin" />
              ) : (
                translate('Move tasks')
              )}
            </Button>
          )}
          {isInitialStep && (
            <Button
              className="btn-submit"
              onClick={checkIfTransfer}
              disabled={loading === 'deleting'}
            >
              {loading === 'deleting' ? (
                <i className="bx bx-loader-alt bx-spin" />
              ) : (
                translate('Exclude')
              )}
            </Button>
          )}
        </ModalFooter>
      )}
    </ModalEditPhaseStyled>
  );
};

const ModalEditPhaseStyled = styled(Modal)`
  min-width: 30rem;

  .btn-submit {
    width: 4.5rem;
    background-color: #f46a6a;
    color: #fff !important;
    border: none;
  }
  .btn-submit:hover {
    background-color: #f23d3d;
  }

  .btn-delete {
    border: none;
    color: #f46a6a !important;

    &:hover {
      border: none !important;
    }
  }
`;

ModalDeletePhase.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  phase: PropTypes.object,
  setColumns: PropTypes.func.isRequired,
  columns: PropTypes.array.isRequired,
  setStatus: PropTypes.func.isRequired,
  kanbanName: PropTypes.string.isRequired,
};

export default ModalDeletePhase;
